import { useState } from 'react';
import { Box, Collapse, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { tokens } from '../../global/theme/tokens';
import { AiMarkdown } from './AiMarkdown';
import { AiReferenceChip } from './AiReferenceChip';
import { parseReferenceAttachment } from './aiChatEntities';
import { entryKind } from '../exo/project-file-explorer/explorerUtils';

const TOOL_RESULT_PREVIEW = 1500;

export const AiChatMessage = ({ message }) =>
  message.role === 'user' ? (
    <UserMessage message={message} />
  ) : (
    <AssistantMessage message={message} />
  );

const UserMessage = ({ message }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  // optimistic messages carry what the composer knew, stored ones the API shape
  const references =
    message.localReferences ||
    (message.attachments || []).map(parseReferenceAttachment).filter(Boolean);
  const files =
    message.localAttachments ||
    (message.attachments || []).map(transcriptFile).filter(Boolean);

  return (
    <Box className="flex flex-col items-end gap-1 pl-8">
      {(references.length > 0 || files.length > 0) && (
        <Box className="flex flex-wrap justify-end gap-1 max-w-full">
          {references.map((ref) => (
            <AiReferenceChip
              key={`${ref.type}-${ref.id}`}
              reference={ref}
              variant="link"
            />
          ))}
          {files.map((file, index) => (
            <FileChip key={index} file={file} />
          ))}
        </Box>
      )}
      <Box
        className="px-3 py-2 max-w-full"
        sx={{
          backgroundColor: `${colors.info}25`,
          border: `1px solid ${colors.info}40`,
          borderRadius: '16px 16px 4px 16px',
          opacity: message.local ? 0.85 : 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: colors.text,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {message.content}
        </Typography>
      </Box>
    </Box>
  );
};

// Stored files arrive as laravel/ai attachments. Project folder files are
// expected as { type: 'project-file', projectId, path, name?, mimeType? },
// the shape proposed to the backend together with projectFiles.
function transcriptFile(attachment) {
  if (attachment.type === 'stored-image' || attachment.type === 'stored-document')
    return { name: attachment.name, isImage: attachment.type === 'stored-image' };
  if (attachment.type === 'project-file')
    return {
      source: 'project',
      name: attachment.name || attachment.path?.split('/').pop(),
      path: attachment.path,
      mimeType: attachment.mimeType,
    };
  return null;
}

const FileChip = ({ file }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const isProjectFile = file.source === 'project';
  const kind = isProjectFile
    ? entryKind({ name: file.name || '', mimeType: file.mimeType })
    : {
        Icon: file.isImage ? ImageIcon : InsertDriveFileIcon,
        color: colors.text,
      };

  return (
    <Tooltip title={isProjectFile ? file.path : ''} disableInteractive>
      <Box
        className="flex items-center gap-1 px-1.5 py-0.5 rounded-lg min-w-0"
        sx={{ backgroundColor: colors.card, border: `1px solid ${colors.bgInput}` }}
      >
        {isProjectFile && <FolderOpenIcon sx={{ fontSize: 13, opacity: 0.5 }} />}
        <kind.Icon sx={{ fontSize: 14, color: kind.color, opacity: 0.7 }} />
        <Typography
          variant="caption"
          noWrap
          sx={{ color: colors.text, maxWidth: 160 }}
        >
          {file.name}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const AssistantMessage = ({ message }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  return (
    <Box className="flex flex-col gap-1 pr-2">
      {message.content && <AiMarkdown content={message.content} />}
      {message.interrupted && (
        <Box className="flex items-center gap-1" sx={{ color: colors.error }}>
          <ErrorOutlineIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" fontWeight={500}>
            {t('Answer interrupted')}
          </Typography>
        </Box>
      )}
      <ToolSteps
        toolCalls={message.toolCalls || []}
        toolResults={message.toolResults || []}
      />
    </Box>
  );
};

const ToolSteps = ({ toolCalls, toolResults }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const steps = [
    ...toolCalls.map((call) => ({
      key: call.id,
      name: call.name,
      args: call.arguments,
      result: toolResults.find((r) => r.id && r.id === call.id),
    })),
    ...toolResults
      .filter((r) => !toolCalls.some((call) => call.id && call.id === r.id))
      .map((result) => ({ key: result.id, name: result.name, result })),
  ];
  if (steps.length === 0) return null;

  const hasWarning = steps.some((s) => s.result?.failed || s.result?.denied);

  return (
    <Box>
      <Box
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-0.5 cursor-pointer select-none rounded-md pr-1.5"
        sx={{
          color: colors.text,
          opacity: 0.6,
          '&:hover': { opacity: 1 },
        }}
      >
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: '0.2s ease',
          }}
        />
        <Typography variant="caption">
          {open ? t('Hide steps') : t('Show steps')} ({steps.length})
        </Typography>
        {hasWarning && (
          <WarningAmberIcon sx={{ fontSize: 14, color: colors.warning, ml: 0.5 }} />
        )}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box className="flex flex-col gap-1.5 mt-1">
          {steps.map((step, index) => (
            <ToolStep key={step.key || index} step={step} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const ToolStep = ({ step }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { result } = step;

  const resultText = result?.result || '';
  const args =
    step.args && Object.keys(step.args).length > 0
      ? JSON.stringify(step.args, null, 2)
      : null;

  const codeSx = {
    m: 0,
    mt: 0.5,
    p: 1,
    maxHeight: 160,
    overflow: 'auto',
    fontSize: '0.7rem',
    fontFamily: 'Source Code Pro, monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    backgroundColor: colors.bgInput,
    borderRadius: '6px',
  };

  return (
    <Box
      className="rounded-lg p-2"
      sx={{ backgroundColor: colors.card, border: `1px solid ${colors.bgInput}` }}
    >
      <Box className="flex items-center gap-2">
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ fontFamily: 'Source Code Pro, monospace' }}
        >
          {step.name || t('Tool')}
        </Typography>
        {result?.failed && (
          <Typography variant="caption" sx={{ color: colors.error }}>
            {t('Failed')}
          </Typography>
        )}
        {result?.denied && (
          <Typography variant="caption" sx={{ color: colors.warning }}>
            {t('Denied')}
          </Typography>
        )}
      </Box>
      {args && (
        <Box component="pre" sx={codeSx}>
          {args}
        </Box>
      )}
      {resultText && (
        <Box component="pre" sx={{ ...codeSx, opacity: 0.8 }}>
          {resultText.length > TOOL_RESULT_PREVIEW
            ? `${resultText.slice(0, TOOL_RESULT_PREVIEW)}…`
            : resultText}
        </Box>
      )}
    </Box>
  );
};
